import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import { useTranslation } from 'react-i18next';

import type { KioskCategory } from './KioskCategoriesTab';
import type { KioskProduct } from './KioskProductsTab';
import {
  KioskCategoryProductTemplate,
  deleteKioskCategoryProductTemplate,
  loadKioskCategoryProductTemplates,
  saveKioskCategoryProductTemplate,
} from '../utils/kioskCategoryProductTemplates';

interface KioskCategoryProductTemplateBarProps {
  /** Şirket id; null ise bar disabled görünür. */
  companyId: string | null;
  /** Şu anki kiosk seçimi (kategori listesi). */
  categories: KioskCategory[];
  /** Şu anki kiosk seçimi (categoryId → ürün listesi). */
  kioskProducts: { [categoryId: string]: KioskProduct[] };
  /** Bir taslak seçildiğinde modal state'ine uygulanır. */
  onApplyTemplate: (template: KioskCategoryProductTemplate) => void;
}

const KioskCategoryProductTemplateBar: React.FC<
  KioskCategoryProductTemplateBarProps
> = ({ companyId, categories, kioskProducts, onApplyTemplate }) => {
  const { t } = useTranslation('salesSystem');

  const [templates, setTemplates] = useState<KioskCategoryProductTemplate[]>(
    []
  );
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveDialogName, setSaveDialogName] = useState('');
  const [saveDialogError, setSaveDialogError] = useState<string | null>(null);

  const refreshTemplates = useCallback(() => {
    if (!companyId) {
      setTemplates([]);
      return;
    }
    setTemplates(loadKioskCategoryProductTemplates(companyId));
  }, [companyId]);

  useEffect(() => {
    refreshTemplates();
  }, [refreshTemplates]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedTemplateId) || null,
    [templates, selectedTemplateId]
  );

  const handleOpenSaveDialog = () => {
    if (!companyId) {
      setSaveDialogError(
        t('kioskSettings.categoryProductTemplate.saveDialog.errors.noCompany')
      );
      return;
    }
    setSaveDialogName(selectedTemplate?.name || '');
    setSaveDialogError(null);
    setSaveDialogOpen(true);
  };

  const handleConfirmSaveTemplate = () => {
    if (!companyId) {
      setSaveDialogError(
        t('kioskSettings.categoryProductTemplate.saveDialog.errors.noCompany')
      );
      return;
    }
    const name = saveDialogName.trim();
    if (!name) {
      setSaveDialogError(
        t('kioskSettings.categoryProductTemplate.saveDialog.errors.emptyName')
      );
      return;
    }
    const saved = saveKioskCategoryProductTemplate(companyId, {
      name,
      categories,
      kioskProducts,
    });
    if (!saved) {
      setSaveDialogError(
        t('kioskSettings.categoryProductTemplate.saveDialog.errors.saveFailed')
      );
      return;
    }
    refreshTemplates();
    setSelectedTemplateId(saved.id);
    setSaveDialogOpen(false);
  };

  const handleApplySelectedTemplate = (templateId: string) => {
    setSelectedTemplateId(templateId);
    if (!templateId) return;
    const tpl = templates.find((t) => t.id === templateId);
    if (!tpl) return;
    onApplyTemplate(tpl);
  };

  const handleDeleteSelectedTemplate = () => {
    if (!companyId || !selectedTemplateId) return;
    const tpl = templates.find((t) => t.id === selectedTemplateId);
    if (!tpl) return;
    const ok = window.confirm(
      t('kioskSettings.categoryProductTemplate.deleteConfirm', {
        name: tpl.name,
      })
    );
    if (!ok) return;
    const success = deleteKioskCategoryProductTemplate(
      companyId,
      selectedTemplateId
    );
    if (success) {
      setSelectedTemplateId('');
      refreshTemplates();
    }
  };

  const disabled = !companyId;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1,
        mb: 1,
        bgcolor: 'grey.50',
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 0.5 }}>
          <BookmarkIcon fontSize="small" color="action" />
          <Typography variant="body2" fontWeight={600}>
            {t('kioskSettings.categoryProductTemplate.barTitle')}
          </Typography>
        </Box>

        <FormControl
          size="small"
          sx={{ minWidth: 240, flex: 1 }}
          disabled={disabled || templates.length === 0}
        >
          <InputLabel id="kiosk-tpl-select-label">
            {t('kioskSettings.categoryProductTemplate.selectPlaceholder')}
          </InputLabel>
          <Select
            labelId="kiosk-tpl-select-label"
            label={t(
              'kioskSettings.categoryProductTemplate.selectPlaceholder'
            )}
            value={selectedTemplateId}
            onChange={(e) =>
              handleApplySelectedTemplate(e.target.value as string)
            }
          >
            <MenuItem value="">
              <em>
                {t('kioskSettings.categoryProductTemplate.selectPlaceholder')}
              </em>
            </MenuItem>
            {templates.map((tpl) => (
              <MenuItem key={tpl.id} value={tpl.id}>
                {tpl.name}
                {' • '}
                {new Date(tpl.updatedAt).toLocaleDateString()}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Tooltip
          title={t('kioskSettings.categoryProductTemplate.deleteButton')}
        >
          <span>
            <IconButton
              size="small"
              color="error"
              disabled={disabled || !selectedTemplateId}
              onClick={handleDeleteSelectedTemplate}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Button
          variant="contained"
          size="small"
          startIcon={<SaveIcon />}
          onClick={handleOpenSaveDialog}
          disabled={disabled}
        >
          {t('kioskSettings.categoryProductTemplate.saveButton')}
        </Button>
      </Box>

      <Dialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {t('kioskSettings.categoryProductTemplate.saveDialog.title')}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label={t(
              'kioskSettings.categoryProductTemplate.saveDialog.nameLabel'
            )}
            placeholder={t(
              'kioskSettings.categoryProductTemplate.saveDialog.namePlaceholder'
            )}
            value={saveDialogName}
            onChange={(e) => {
              setSaveDialogName(e.target.value);
              if (saveDialogError) setSaveDialogError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleConfirmSaveTemplate();
              }
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {t(
              'kioskSettings.categoryProductTemplate.saveDialog.overwriteHint'
            )}
          </Typography>
          {saveDialogError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {saveDialogError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>
            {t('kioskSettings.categoryProductTemplate.saveDialog.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmSaveTemplate}
            startIcon={<SaveIcon />}
          >
            {t('kioskSettings.categoryProductTemplate.saveDialog.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default KioskCategoryProductTemplateBar;
